import React, { useState, useEffect, useCallback } from "react";
import { BrowserRouter as Router } from "react-router-dom";
import AdminRoutes from "./routes/AdminRoutes"; // Main admin route component
import Navbar from "./components/Navbar/Navbar"; // Admin navbar
import Footer from "./components/Footer/Footer";
import { ConfirmDialogProvider } from "./components/ConfirmDialog/ConfirmDialogProvider";
import {
  clearStoredAdminSession,
  fetchAdminProfile,
  getStoredAdminRole,
  setStoredAdminRole,
} from "./utils/adminAuth";

import './App.css';

const ADMIN_REFRESH_INTERVAL_MS = 45 * 60 * 1000;
const isUsableStoredToken = (value) => Boolean(value && value !== "undefined" && value !== "null");
const getStoredToken = (key) => {
  const value = localStorage.getItem(key);
  return isUsableStoredToken(value) ? value : null;
};

const isJwtExpired = (token) => {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    if (!payload?.exp) return false;
    return payload.exp * 1000 <= Date.now();
  } catch (_) {
    return true;
  }
};

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [adminRole, setAdminRole] = useState(() => getStoredAdminRole());

  const refreshAdminTokens = useCallback(async () => {
    const refreshToken = getStoredToken("refreshToken");
    if (!refreshToken) return false;

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/refresh-token`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken }),
      });

      if (!response.ok) return false;

      const data = await response.json();
      if (!data?.idToken || !data?.accessToken) return false;

      localStorage.setItem("idToken", data.idToken);
      localStorage.setItem("accessToken", data.accessToken);
      if (data.refreshToken) {
        localStorage.setItem("refreshToken", data.refreshToken);
      }
      return true;
    } catch (_) {
      return false;
    }
  }, []);

  // Bootstrap auth state on mount with refresh fallback
  useEffect(() => {
    const bootstrapAuth = async () => {
      const idToken = getStoredToken("idToken");
      const refreshToken = getStoredToken("refreshToken");

      if (idToken && !isJwtExpired(idToken)) {
        setIsAuthenticated(true);
        return;
      }

      if (refreshToken) {
        const refreshed = await refreshAdminTokens();
        setIsAuthenticated(refreshed);
        return;
      }

      setIsAuthenticated(false);
    };

    bootstrapAuth();
  }, [refreshAdminTokens]);

  useEffect(() => {
    if (!isAuthenticated) {
      setAdminRole(null);
      setStoredAdminRole(null);
      return undefined;
    }

    let isActive = true;

    const loadAdminRole = async () => {
      const profile = await fetchAdminProfile({
        onAuthFailure: () => {
          clearStoredAdminSession();
          if (isActive) {
            setAdminRole(null);
            setIsAuthenticated(false);
          }
        },
      });

      if (!isActive) {
        return;
      }

      setAdminRole(profile?.role || getStoredAdminRole());
    };

    loadAdminRole();

    return () => {
      isActive = false;
    };
  }, [isAuthenticated]);

  // Keep admin session alive while refresh token is valid
  useEffect(() => {
    if (!isAuthenticated) return undefined;

    const intervalId = setInterval(async () => {
      await refreshAdminTokens();
    }, ADMIN_REFRESH_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [isAuthenticated, refreshAdminTokens]);

  // Handle user login, store tokens in localStorage
  const handleLogin = (idToken, accessToken, refreshToken) => {
    if (idToken) localStorage.setItem("idToken", idToken);
    if (accessToken) localStorage.setItem("accessToken", accessToken);
    if (refreshToken) localStorage.setItem("refreshToken", refreshToken);
    setAdminRole(null);
    setIsAuthenticated(Boolean(idToken || getStoredToken("idToken")));
  };

  // Handle user logout, clear tokens from localStorage
  const handleLogout = () => {
    clearStoredAdminSession();
    setAdminRole(null);
    setIsAuthenticated(false);
  };

  return (
    <Router>
      <ConfirmDialogProvider>
        <div className="App">
          {/* Navbar receives isAuthenticated and handleLogout */}
          <Navbar
            isAuthenticated={isAuthenticated}
            handleLogout={handleLogout}
            adminRole={adminRole}
          />
          
          {/* AdminRoutes receives isAuthenticated and handleLogin */}
          <AdminRoutes
            isAuthenticated={isAuthenticated}
            onLogin={handleLogin}
            adminRole={adminRole}
          />
          <Footer />  {/* Add Footer component */}

        </div>
      </ConfirmDialogProvider>
    </Router>
  );
}

export default App;
